import { z } from 'zod';

/**
 * Zod refinement helper to validate that a string is in camelCase format
 * (no snake_case underscores allowed, only camelCase)
 *
 * @param message - Error message to display if validation fails
 * @returns A Zod refinement function
 *
 * @example
 * ```ts
 * const schema = z.object({
 *   code: z.string().refine(camelCaseValidator('Code must be in camelCase'), {
 *     message: 'Code must be in camelCase',
 *   }),
 * });
 * ```
 */
export const camelCaseValidator =
  (_message: string) =>
  (value: string): boolean => {
    // Empty strings are handled by other validators (min, required, etc.)
    if (!value) {
      return true;
    }

    // Check that string doesn't contain underscores
    if (value.includes('_')) {
      return false;
    }

    // Check that string follows camelCase pattern:
    // - Must start with a lowercase letter
    // - Can contain letters (both cases) and numbers
    // - No underscores, spaces, or special characters
    const camelCasePattern = /^[a-z][a-zA-Z0-9]*$/;

    return camelCasePattern.test(value);
  };

/**
 * Zod-схема строки с проверкой формата camelCase и границ длины.
 *
 * @param options - Настройки схемы
 * @param options.message - Сообщение о нарушении формата camelCase
 * @param options.minLength - Минимальная длина
 * @param options.maxLength - Максимальная длина
 * @param options.minLengthMessage - Сообщение при нарушении минимальной длины; без него zod показывает своё техническое сообщение
 * @param options.maxLengthMessage - Сообщение при нарушении максимальной длины; без него zod показывает своё техническое сообщение
 * @returns Zod-строка с проверкой camelCase
 *
 * @example
 * ```ts
 * const schema = z.object({
 *   code: zodCamelCaseString({
 *     message: 'Идентификатор должен быть в формате camelCase',
 *     minLength: 1,
 *     minLengthMessage: 'Обязательное поле',
 *   }),
 * });
 * ```
 */
export const zodCamelCaseString = (options?: {
  message?: string;
  minLength?: number;
  maxLength?: number;
  minLengthMessage?: string;
  maxLengthMessage?: string;
}) => {
  const {
    message = 'Must be in camelCase format (no underscores allowed)',
    minLength,
    maxLength,
    minLengthMessage,
    maxLengthMessage,
  } = options || {};

  let schema = z.string();

  if (minLength !== undefined) {
    schema = schema.min(minLength, minLengthMessage);
  }

  if (maxLength !== undefined) {
    schema = schema.max(maxLength, maxLengthMessage);
  }

  return schema.refine(camelCaseValidator(message), {
    message,
  });
};

/**
 * Zod refinement helper: проверяет, что строка записана в формате snake_case
 * (строчные латинские буквы, цифры и подчёркивания, начинается с буквы).
 *
 * @param _message - Сообщение об ошибке (используется на уровне схемы)
 * @returns Функция-предикат для z.refine
 */
export const snakeCaseValidator =
  (_message: string) =>
  (value: string): boolean => {
    // Пустые строки покрываются другими валидаторами (min, required и т.п.)
    if (!value) {
      return true;
    }

    // Начинается со строчной буквы, далее строчные буквы, цифры и подчёркивания
    const snakeCasePattern = /^[a-z][a-z0-9_]*$/;

    return snakeCasePattern.test(value);
  };

/**
 * Zod-схема строки с проверкой формата snake_case.
 *
 * @param options - Настройки: сообщение об ошибке и границы длины
 * @returns Zod-строка с проверкой snake_case
 */
export const zodSnakeCaseString = (options?: { message?: string; minLength?: number; maxLength?: number }) => {
  const { message = 'Must be in snake_case format (lowercase letters, digits and underscores)', minLength, maxLength } =
    options || {};

  let schema = z.string();

  if (minLength !== undefined) {
    schema = schema.min(minLength);
  }

  if (maxLength !== undefined) {
    schema = schema.max(maxLength);
  }

  return schema.refine(snakeCaseValidator(message), {
    message,
  });
};
